import React, { useState, useEffect, useCallback } from 'react';
import { orderService } from '../services/api';
import { GlassPanel, GlassButton, GlassCard, PageHeader } from '../components/GlassUI';
import StatusBadge from '../components/StatusBadge';
import { FaRupeeSign, FaCalendar, FaPlus, FaBoxOpen } from 'react-icons/fa';

interface OrdersPageProps {
  onNavigate?: (page: string, id?: string) => void;
}

const STAGE_TABS = [
  { key: 'all', label: 'All' },
  { key: 'STAGE_1_PLACEMENT', label: 'Stage 1 - Placed' },
  { key: 'STAGE_2_KARIGAR', label: 'Stage 2 - Karigar' },
  { key: 'STAGE_3_QC', label: 'Stage 3 - QC' },
  { key: 'STAGE_4_LEDGER', label: 'Stage 4 - Ledger' },
  { key: 'STAGE_5_DELIVERY', label: 'Stage 5 - Delivered' },
];

const PayChips: React.FC<{ summary?: any; className?: string }> = ({ summary, className = '' }) => {
  if (!summary || (summary.paidCount || 0) + (summary.unpaidCount || 0) === 0) return null;
  return (
    <span className={`inline-flex items-center gap-1 flex-wrap ${className}`}>
      {summary.totalPaid > 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
          PAID ₹{Number(summary.totalPaid).toLocaleString('en-IN')}
        </span>
      )}
      {summary.totalUnpaid > 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
          DUE ₹{Number(summary.totalUnpaid).toLocaleString('en-IN')}
        </span>
      )}
      {summary.totalPaid > 0 && summary.totalUnpaid === 0 && (
        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">SETTLED</span>
      )}
    </span>
  );
};

export const OrdersPage: React.FC<OrdersPageProps> = ({ onNavigate }) => {
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [activeStage, setActiveStage] = useState('all');

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      let response;
      if (activeStage === 'all') {
        response = await orderService.getAll(undefined, page, 20);
      } else {
        response = await orderService.getOrdersByStage(activeStage, page, 20);
      }
      setOrders(response.data.orders || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error loading orders:', error);
    } finally {
      setLoading(false);
    }
  }, [page, activeStage]);

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleStageChange = (stage: string) => {
    setActiveStage(stage);
    setPage(1);
  };

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <PageHeader
        title="Orders"
        subtitle={`${total} total orders`}
        icon={<FaBoxOpen />}
        action={
          <GlassButton variant="primary" onClick={() => onNavigate?.('gold-sale')} className="w-full sm:w-auto">
            <FaPlus /> New Order
          </GlassButton>
        }
      />

      {/* Filter Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {STAGE_TABS.map((tab) => (
          <button
            key={tab.key}
            onClick={() => handleStageChange(tab.key)}
            className={`px-4 py-2 rounded-lg font-semibold transition-all border whitespace-nowrap text-sm ${
              activeStage === tab.key
                ? 'bg-slate-900 border-slate-900 text-white'
                : 'bg-white border-slate-200 text-slate-700 hover:bg-slate-100'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-4 border-slate-300 border-t-slate-900 rounded-full" />
        </div>
      )}

      {/* Orders - Mobile: Cards, Desktop: Table */}
      {!loading && orders.length > 0 && (
        <>
          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {orders.map((order: any) => (
              <GlassCard
                key={order.id}
                className="cursor-pointer hover:shadow-xl transition-shadow"
                onClick={() => onNavigate?.('order-detail', order.id)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-bold text-slate-900 text-sm truncate">{order.invoiceNumber}</p>
                    <p className="text-slate-500 text-xs">{order.customer?.customerName || 'N/A'}</p>
                  </div>
                  <StatusBadge
                    stage={order.currentStage}
                    type="order"
                    karigarDelayed={order.karigarDelayed}
                    className="text-xs shrink-0 ml-2"
                  />
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-600 flex items-center gap-1">
                    <FaRupeeSign size={10} className="text-amber-500" />
                    {(order.totalAmount || order.finalAmount || 0).toFixed(2)}
                  </span>
                  <span className="text-slate-400 text-xs flex items-center gap-1">
                    <FaCalendar size={10} />
                    {new Date(order.orderDate).toLocaleDateString()}
                  </span>
                </div>
                <PayChips summary={order.paymentSummary} className="mt-2" />
              </GlassCard>
            ))}
          </div>

          {/* Desktop Table */}
          <GlassPanel className="p-6 overflow-x-auto hidden md:block">
            <table className="w-full text-slate-700">
              <thead>
                <tr className="border-b border-slate-200">
                  <th className="text-left py-3 px-4 text-sm font-semibold">Invoice</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Customer</th>
                  <th className="text-right py-3 px-4 text-sm font-semibold">Amount</th>
                  <th className="text-center py-3 px-4 text-sm font-semibold">Stage</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Payments</th>
                  <th className="text-left py-3 px-4 text-sm font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order: any) => (
                  <tr
                    key={order.id}
                    className="border-b border-slate-200 hover:bg-slate-50 cursor-pointer transition-all"
                    onClick={() => onNavigate?.('order-detail', order.id)}
                  >
                    <td className="py-3 px-4 font-semibold text-sm">{order.invoiceNumber}</td>
                    <td className="py-3 px-4 text-sm">{order.customer?.customerName || 'N/A'}</td>
                    <td className="py-3 px-4 text-right text-sm flex items-center justify-end gap-1">
                      <FaRupeeSign className="text-amber-500" size={12} />
                      {(order.totalAmount || order.finalAmount || 0).toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <StatusBadge
                        stage={order.currentStage}
                        type="order"
                        karigarDelayed={order.karigarDelayed}
                        className="text-xs"
                      />
                    </td>
                    <td className="py-3 px-4">
                      <PayChips summary={order.paymentSummary} />
                    </td>
                    <td className="py-3 px-4 text-sm">
                      <span className="flex items-center gap-2">
                        <FaCalendar className="text-blue-400" size={12} />
                        {new Date(order.orderDate).toLocaleDateString()}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </GlassPanel>
        </>
      )}

      {!loading && orders.length === 0 && (
        <GlassCard className="text-center py-12">
          <p className="text-slate-400 text-lg">No orders found</p>
        </GlassCard>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center items-center gap-2 mt-8">
          <GlassButton
            onClick={() => setPage(Math.max(1, page - 1))}
            variant="secondary"
            disabled={page === 1}
          >
            Previous
          </GlassButton>
          <GlassCard className="px-4 py-2 text-slate-900 text-sm">
            Page {page} of {Math.ceil(total / 20)}
          </GlassCard>
          <GlassButton
            onClick={() => setPage(page + 1)}
            variant="secondary"
            disabled={page * 20 >= total}
          >
            Next
          </GlassButton>
        </div>
      )}
    </div>
  );
};

export default OrdersPage;
